import React from "react";

import { Tooltip as AntTooltip, TooltipProps as AntTooltipProps } from "antd";

export type TooltipVariant = "brand" | "neutral" | "compact";

type TooltipProps = AntTooltipProps & {
  disabled?: boolean;
  className?: string;
  mRef?: React.RefObject<HTMLSpanElement>;

  // `brand` (current default): green shell (#065c45) and white body text
  // `neutral`: white shell with gray border; Ant `color` preset is omitted. Consumer `color` is ignored when neutral.
  // `compact`: the design-library tooltip — small dark-green pill, 10px white text, no arrow. Styling lives in
  // `tooltips.scss` under `.tooltip-variant-compact`, so consumer `color` is ignored here too.
  variant?: TooltipVariant;
};

const BRAND_BACKGROUND = "#065c45";
const NEUTRAL_BODY_TEXT = "#100c06"; // `variables.$black`

// `brand` styles itself through Ant's `color` preset, so it has no class of its own
const VARIANT_ROOT_CLASS_NAMES: Partial<Record<TooltipVariant, string>> = {
  neutral: "tooltip-variant-neutral",
  compact: "tooltip-variant-compact",
};

export const Tooltip = ({
  mRef,
  style,
  className,
  variant = "brand",
  styles: stylesProp,
  rootClassName,
  color: colorProp,
  disabled,
  ...props
}: TooltipProps) => {
  // `compact` sets its colour in the stylesheet with the rest of its typography; an inline one would override it
  const mergedStyles =
    variant === "compact"
      ? stylesProp
      : {
          ...stylesProp,
          body: { color: variant === "neutral" ? NEUTRAL_BODY_TEXT : "#fff", ...stylesProp?.body },
        };

  const mergedRootClassName = [VARIANT_ROOT_CLASS_NAMES[variant], rootClassName].filter(Boolean).join(" ") || undefined;

  // Easy way to remove tooltips in certain contexts
  if (disabled) {
    return (
      <span ref={mRef} style={{ display: "inline-block", ...style }}>
        {props.children}
      </span>
    );
  }

  return (
    <AntTooltip
      {...(variant === "brand" ? { color: colorProp ?? BRAND_BACKGROUND } : {})}
      // the design has no triangular tail
      {...(variant === "compact" ? { arrow: false } : {})}
      zIndex={10002}
      styles={mergedStyles}
      destroyOnHidden={true}
      mouseLeaveDelay={0.1}
      mouseEnterDelay={0.3}
      {...props}
      {...(mergedRootClassName ? { rootClassName: mergedRootClassName } : {})}
    >
      <span ref={mRef} style={{ display: "inline-block", ...style }} className={className}>
        {props.children}
      </span>
    </AntTooltip>
  );
};

export default Tooltip;
